import { useState } from 'react';
import type { ErpConnectionView, ErpReadinessStep } from '../../api';
import { erpApi } from '../../api';
import { PanelCard, SectionHeader } from '../project/ui';
import { Checklist, Field, Input } from '../primitives';
import type { ChecklistItem } from '../primitives';
import { ErrorNotice } from '../ui';
import { Timestamp } from '../status/StatusPieces';
import { readinessLabel, readinessMissing, readinessStepOf, readinessWho } from './labels';
import { useAction } from './useAction';

/**
 * What sending invoices needs, each step from a fact the connection shows
 * (the server reads them; this only draws them). A step the server could not
 * read is not done. Once sending is on and nothing is wrong the card goes
 * quiet: a healthy state needs no checklist.
 *
 * The company is confirmed here, by typing its fiscal code: a person who has
 * checked it in the ERP says it is the company the documents are made out by.
 * The ERP offers no way to look the company up, so this is a person's word,
 * kept with their name and the time.
 */
export function ErpDocumentReadiness({
  connection,
  canManage,
  onChanged,
}: {
  connection: ErpConnectionView;
  canManage: boolean;
  onChanged: () => void;
}) {
  const steps = connection.documents_readiness ?? [];
  const action = useAction();
  const [typed, setTyped] = useState('');
  if (steps.length === 0) return null;

  const mode = connection.settings.documents.mode;
  const trouble = steps.some((step) => step.state === 'attention' || step.state === 'blocked' || step.state === 'unknown');
  if (mode === 'live' && readinessMissing(steps).length === 0 && !trouble) return null;

  const company = readinessStepOf(steps, 'company');
  const confirming = canManage && company !== undefined && company.state !== 'done';
  const erpName = connection.provider_info.display_name;

  const items: ChecklistItem[] = steps.map((step) => ({
    key: step.id,
    label: readinessLabel[step.id],
    state: step.state === 'done' ? 'done' : 'todo',
    note: noteOf(step),
    action:
      step.id === 'test' && step.state !== 'done' && connection.credential_configured && canManage ? (
        <button
          type="button"
          className="button button--secondary button--small"
          disabled={action.busy !== null}
          data-testid="erp-readiness-test"
          onClick={() =>
            void action.run('test', async () => {
              await erpApi.testConnection(connection.id);
              onChanged();
            })
          }
        >
          {action.busy === 'test' ? 'Testing' : 'Test connection'}
        </button>
      ) : undefined,
  }));

  const typedMatches = normalizeCode(typed) !== '' && normalizeCode(typed) === normalizeCode(connection.company_identifier);

  return (
    <PanelCard labelledBy="erp-readiness-title" testId="erp-readiness">
      <SectionHeader id="erp-readiness-title" title="Before sending" />
      <Checklist label="What sending needs" items={items} testId="erp-readiness-list" />
      {confirming ? (
        <form
          className="erp-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!typedMatches) return;
            void action.run('confirm', async () => {
              await erpApi.updateConnection(connection.id, { confirm_company: typed.trim() });
              setTyped('');
              onChanged();
            });
          }}
        >
          <Field
            label={`Type the company’s fiscal code to confirm it is the one in ${erpName}`}
            error={typed.trim() !== '' && !typedMatches ? `Not ${connection.company_identifier}` : null}
          >
            <Input
              id="erp-confirm-company"
              value={typed}
              autoComplete="off"
              spellCheck={false}
              maxLength={64}
              placeholder={connection.company_identifier}
              onChange={(event) => setTyped(event.target.value)}
            />
          </Field>
          <button
            type="submit"
            className="button button--secondary"
            disabled={!typedMatches || action.busy !== null}
            data-testid="erp-confirm-company-submit"
          >
            {action.busy === 'confirm' ? 'Confirming' : 'Confirm company'}
          </button>
        </form>
      ) : null}
      <ErrorNotice error={action.error} />
    </PanelCard>
  );
}

/** A fiscal code compared without spaces, case or the RO attribute. */
function normalizeCode(value: string): string {
  const code = value.replace(/\s+/g, '').toUpperCase();
  return /^RO\d/.test(code) ? code.slice(2) : code;
}

/** The evidence next to a step: when it was gathered, what was read, or who acts. */
function noteOf(step: ErpReadinessStep) {
  const who = step.state === 'done' ? undefined : readinessWho[step.id];
  const parts: string[] = [];
  if (step.detail) parts.push(step.detail);
  if (who && !step.detail) parts.push(who);
  if (step.state === 'unknown' && parts.length === 0) parts.push('Not known');
  if (!step.at && parts.length === 0) return undefined;
  return (
    <>
      {step.at ? <Timestamp iso={step.at} /> : null}
      {step.at && parts.length > 0 ? ' · ' : null}
      {parts.join(' · ')}
    </>
  );
}
